import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Pagination,
  Select,
  Switch,
  Table,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { FiSearch } from 'react-icons/fi';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import bannerService from '../../services/bannerService';
import BannerCreateDrawer from './BannerCreateDrawer';
import {
  BANNER_TYPE_LABELS,
  BANNER_TYPE_OPTIONS,
  PORTAL_SCOPE_LABELS,
  PORTAL_SCOPE_OPTIONS,
  getBannerListColumns,
} from './banners.helpers';

export default function BannersPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.BANNERS_CREATE);
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.BANNERS_EDIT);
  const canDelete = isSuperAdmin || hasPermission(PERMISSIONS.BANNERS_DELETE);

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [portalFilter, setPortalFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadBanners = useCallback(async () => {
    setLoading(true);
    try {
      const items = await bannerService.listBanners({ includeInactive });
      setBanners(items);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Failed to load banners.');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const q = searchInput.trim().toLowerCase();
  const filtered = useMemo(() => {
    return banners.filter((b) => {
      if (q && !(b.name || '').toLowerCase().includes(q)) return false;
      if (typeFilter && b.banner_type !== typeFilter) return false;
      if (portalFilter && b.portal_scope !== portalFilter) return false;
      return true;
    });
  }, [banners, q, typeFilter, portalFilter]);

  const pagedStart = (page - 1) * pageSize;
  const paged = filtered.slice(pagedStart, pagedStart + pageSize);

  useEffect(() => {
    setPage(1);
  }, [searchInput, typeFilter, portalFilter, includeInactive]);

  const handleUpdateField = useCallback(async (row, patch) => {
    setUpdatingId(row.id);
    try {
      await bannerService.updateBanner(row.id, patch);
      message.success('Banner updated.');
      loadBanners();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Update failed.');
    } finally {
      setUpdatingId(null);
    }
  }, [loadBanners]);

  const handleDelete = useCallback(async (row) => {
    setDeletingId(row.id);
    try {
      await bannerService.deleteBanner(row.id);
      message.success('Banner deleted.');
      loadBanners();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  }, [loadBanners]);

  const columns = useMemo(
    () =>
      getBannerListColumns({
        canEdit,
        canDelete,
        onUpdateField: handleUpdateField,
        onDelete: handleDelete,
        updatingId,
        deletingId,
      }),
    [canEdit, canDelete, handleUpdateField, handleDelete, updatingId, deletingId],
  );

  const typeFilterOptions = [
    { value: '', label: 'All types' },
    ...BANNER_TYPE_OPTIONS.map((o) => ({ value: o.value, label: BANNER_TYPE_LABELS[o.value] || o.label })),
  ];

  const portalFilterOptions = [
    { value: '', label: 'All portals' },
    ...PORTAL_SCOPE_OPTIONS.map((o) => ({
      value: o.value,
      label: PORTAL_SCOPE_LABELS[o.value] || o.label,
    })),
  ];

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Banners</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage promotional, festive, external, and basket banners across investor and partner portals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button icon={<ReloadOutlined />} onClick={loadBanners} loading={loading}>
            Refresh
          </Button>
          {canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawerOpen(true)}>
              New banner
            </Button>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-100 flex flex-col gap-3 bg-gray-50/50">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative w-full lg:max-w-md">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full h-[38px] pl-9 pr-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all bg-white"
              />
            </div>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              options={typeFilterOptions}
              className="w-full sm:w-48"
              placeholder="Filter by type"
            />
            <Select
              value={portalFilter}
              onChange={setPortalFilter}
              options={portalFilterOptions}
              className="w-full sm:w-48"
              placeholder="Filter by portal"
            />
            <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
              <Switch checked={includeInactive} onChange={setIncludeInactive} size="small" />
              Include inactive
            </label>
          </div>
          <p className="text-xs text-gray-500">
            {filtered.length} of {banners.length} banners
            {q || typeFilter || portalFilter ? ' (filtered)' : ''}
          </p>
        </div>

        <div className="overflow-x-auto">
          <Table
            columns={columns}
            dataSource={paged}
            rowKey={(r) => r.id}
            loading={loading}
            pagination={false}
            scroll={{ x: 1100 }}
            className="min-w-[900px]"
          />
        </div>

        <div className="flex items-center justify-end px-4 py-3 border-t border-gray-100">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={filtered.length}
            showSizeChanger
            pageSizeOptions={['10', '15', '30', '50']}
            onChange={(p, ps) => {
              setPage(p);
              if (ps !== pageSize) {
                setPageSize(ps);
                setPage(1);
              }
            }}
            showTotal={(total, range) => (
              <span className="text-xs text-gray-500">
                {range[0]}–{range[1]} of {total}
              </span>
            )}
          />
        </div>
      </div>

      {canCreate ? (
        <BannerCreateDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onCreated={loadBanners}
        />
      ) : null}
    </DashboardPageWrapper>
  );
}
